/* eslint-disable import/prefer-default-export */
import { DraggableAttributes } from '@dnd-kit/core'
import { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities'
import cn from 'classnames'
import { useRouter } from 'next/router'
import { forwardRef, useCallback, useRef, useState } from 'react'

import styles from './Asset.module.scss'

import RichTextEditor, { useRichTextEditor } from '~common/RichTextEditor'
import Button from '~elements/Button'
import Select from '~elements/SelectV2'
import { AssetTypeOption } from '~entities/assets/model'
import { SendForApprovalModal } from '~features/assets/approve-asset'
import { useUpdateAssetApproverMutation } from '~generated/hooks'
import { AssetFragment, TagFragment } from '~generated/operations'
import { AssetActionTypes, AssetTypes, TagTypes } from '~generated/types'
import DndIcon from '~icons/dnd.svg'
import MoreIcon from '~icons/more.svg'
import PlusIcon from '~icons/plus.svg'
import SendIcon from '~icons/send.svg'
import { getDateMonthStrDayShortYearFull } from '~lib/dateUtils'
import { stopPropagation } from '~lib/helpers'
import useCookie from '~lib/hooks/useCookie'
import usePortalRoots from '~lib/hooks/usePortalRoots'
import { parseQueryParam } from '~lib/routes'
import Avatar from '~lib/ui-kit/Avatar'
import Col from '~lib/ui-kit/Col'
import EditableText from '~lib/ui-kit/EditableText'
import Option from '~lib/ui-kit/Option'
import Row from '~lib/ui-kit/Row'
import UploadAssetsInput from '~modules/assetModules/containers/UploadAssetsInput'
import Approvals from '~modules/assetModules/elements/Approvals'
import ApproversMenu from '~modules/assetModules/elements/ApproversMenu'
import AssetCardLayout from '~modules/assetModules/elements/AssetCardLayout'
import AssetListItemLayout from '~modules/assetModules/elements/AssetListItemLayout/AssetListItemLayout'
import AssetMenu from '~modules/assetModules/elements/AssetMenu'
import Editor from '~modules/assetModules/elements/Editor'
import useDeleteAsset from '~modules/assetModules/usecases/useDeleteAsset'
import useDownloadAssetFiles from '~modules/assetModules/usecases/useDownloadAssetFiles'
import useGenerateCopyWithAIModal from '~modules/assetModules/usecases/useGenerateCopyWithAIModal'
import useRequestEditAccess from '~modules/assetModules/usecases/useRequestEditAccess'
import useUpdateAssetContent from '~modules/assetModules/usecases/useUpdateAssetContent'
import useUpdateAssetName from '~modules/assetModules/usecases/useUpdateAssetName'
import useUpdateAssetTags from '~modules/assetModules/usecases/useUpdateAssetTags'
import useUpdateAssetType from '~modules/assetModules/usecases/useUpdateAssetType'
import useUpdateAssets from '~modules/assetModules/usecases/useUpdateAssets'
import TagsSelect from '~modules/tags/containers/TagsSelect'
import useCreateTag from '~modules/tags/usecases/useCreateTag'
import { LaunchNavigation, OrganizationNavigation } from '~shared/navigation'
import Divider from '~shared/ui/Divider'

export type AssetDisplayType = 'CARD' | 'LIST'

export interface AssetProps {
  asset: AssetFragment
  viewMode?: boolean
  readOnly?: boolean
  className?: string
  drag?: boolean
  dndAttributes?: DraggableAttributes
  dndListeners?: SyntheticListenerMap
  typeOptions?: AssetTypeOption[]
  tags?: TagFragment[]
  publicPage?: boolean
  brandAsset?: boolean
  launchTags?: TagFragment[] // TODO - get requirements and encapsulate tags logic
  displayType: AssetDisplayType
  copilotView?: boolean
  from?: string
}

// TODO refactor and split by segments
export const Asset = forwardRef<HTMLElement, AssetProps>(
  (
    {
      asset,
      brandAsset,
      className,
      dndAttributes,
      dndListeners,
      drag,
      launchTags,
      publicPage,
      tags = [],
      typeOptions = [],
      readOnly,
      viewMode,
      displayType,
      copilotView,
      ...props
    },
    ref
  ) => {
    const [isApprovalModalOpened, setIsApprovalModalOpened] = useState(false)

    const handleOpenApprovalModal = useCallback(() => {
      setIsApprovalModalOpened(true)
    }, [])

    const handleCloseApprovalModal = useCallback(() => {
      setIsApprovalModalOpened(false)
    }, [])

    const { userId } = useCookie()

    const router = useRouter()
    const launchId = parseQueryParam(router.query, 'launchId')
    const organizationSlug = parseQueryParam(router.query, 'organizationSlug')

    const { id, tagsIds, lastEditBy, userInput: content, assets, type, approverDetails, lastEditDetails } = asset

    const hasAssets = assets?.length > 0

    const [editorProps, editorActions] = useRichTextEditor()
    const { generateWithAI } = useGenerateCopyWithAIModal(editorActions.appendText)

    const [assetOpen, setAssetOpen] = useState(false)
    const handleAssetOpen = () => {
      setAssetOpen((prev) => !prev)
    }

    const [updateAssetName] = useUpdateAssetName(id)

    const [name, setName] = useState(asset.name)
    const [updateAssetType] = useUpdateAssetType(id, hasAssets)
    const [updateAssetTags] = useUpdateAssetTags(id)

    const [updateAssets] = useUpdateAssets(id, type)
    const [requestEditAccess] = useRequestEditAccess()

    const [deleteAsset] = useDeleteAsset()

    const [downloadAssets] = useDownloadAssetFiles(assets)

    const [createTag] = useCreateTag()

    const handleRequestEditAccess = useCallback(() => {
      requestEditAccess()
    }, [requestEditAccess])

    const handleShowDetails = useCallback(() => {
      // TODO - pass href from outside to make component page independent
      if (brandAsset) {
        router.push({
          pathname: OrganizationNavigation.organization.assets.brand.asset.route.pathname,
          query: { organizationSlug, assetId: id },
        })
      } else if (!launchId) {
        router.push({
          pathname: OrganizationNavigation.organization.assets.asset.route.pathname,
          query: { organizationSlug, assetId: id },
        })
      } else {
        router.push({
          pathname: LaunchNavigation.launch.assets.asset.route.pathname,
          query: { organizationSlug, launchId, assetId: id },
        })
      }
    }, [brandAsset, id, launchId, organizationSlug, router])

    const [menuButtonEl, setMenuButtonEl] = useState<HTMLButtonElement>(null)
    const assetsInputRef = useRef<HTMLInputElement>()

    const [updateApprover] = useUpdateAssetApproverMutation()

    const handleApproveAsset = useCallback(() => {
      updateApprover({
        variables: {
          input: {
            assetId: id,
            userIds: [userId],
            action: AssetActionTypes.APPROVE_ASSET,
          },
        },
      })
    }, [id, updateApprover, userId])

    const showApproveBtn = approverDetails?.length > 0

    const hasContent = hasAssets || content?.length > 0

    const [updateAssetContent] = useUpdateAssetContent(id)

    const onGenerateWithAIClickHandler = useCallback(
      (ev: React.MouseEvent<HTMLElement>) => {
        stopPropagation(ev)
        generateWithAI()
      },
      [generateWithAI]
    )

    const onNameChangeHandler = useCallback(
      (ev: React.ChangeEvent<HTMLTextAreaElement>) => {
        const name = ev.target.value
        setName(name)
        updateAssetName(name)
      },
      [updateAssetName]
    )

    const onRequestEditAccessClickHandler = useCallback(
      (ev: React.MouseEvent<HTMLElement>) => {
        stopPropagation(ev)
        handleRequestEditAccess()
      },
      [handleRequestEditAccess]
    )

    const onDelete = useCallback(() => {
      deleteAsset(id)
    }, [deleteAsset, id])

    const onUploadClick = useCallback(() => {
      assetsInputRef.current?.click()
    }, [])

    const [loading, setLoading] = useState(false)

    const handleLoadingChange = useCallback((isLoading: boolean) => {
      setLoading(isLoading)
    }, [])

    const { popupRoot } = usePortalRoots()

    if (displayType === 'LIST') {
      return (
        <>
          <AssetListItemLayout
            {...props}
            ref={ref}
            {...dndAttributes}
            {...dndListeners}
            assetOpen={assetOpen}
            onAssetOpen={handleAssetOpen}
            assetType={
              <Select
                readOnly={viewMode || readOnly}
                highlight
                renderIcon={false}
                label="Asset type"
                variant="plain"
                value={type}
                onChange={updateAssetType}
                onClick={stopPropagation}
                tabIndex={0}
              >
                {typeOptions.map((option) => (
                  <Option key={option.value} value={option.value} icon={option.getIcon()}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            }
            assets={
              <UploadAssetsInput
                readOnly={readOnly || viewMode}
                ref={assetsInputRef}
                value={assets}
                onChange={updateAssets}
              />
            }
            editor={
              <RichTextEditor
                initialContent={content}
                tableEnabled
                googleDocsEnabled
                confluenceEnabled
                readOnly={readOnly || viewMode}
                placeholder="Drag and drop a file, write copy, or add a link..."
                onChange={updateAssetContent}
                onCreate={editorProps.onCreate}
                onDestroy={editorProps.onDestroy}
                onClick={stopPropagation}
              />
            }
            headerMenu={
              <Row align="center" gutter="1">
                {!copilotView && (
                  <TagsSelect
                    appendTo={popupRoot}
                    value={tagsIds}
                    displayLimit={3}
                    variant="dark"
                    displayTags
                    tags={tags}
                    type={TagTypes.ASSET}
                    onClick={stopPropagation}
                    onChange={updateAssetTags}
                    onCreate={createTag}
                    readOnly={readOnly || viewMode}
                  />
                )}

                {readOnly && !viewMode && (
                  <Col>
                    <Button size="s" variant="plainLavender" onClick={handleRequestEditAccess}>
                      Get edit access
                    </Button>
                  </Col>
                )}

                {!readOnly && showApproveBtn && !readOnly && (
                  <>
                    <Col alignSelf="stretch">
                      <Divider direction="vertical" />
                    </Col>

                    <Col>
                      <ApproversMenu
                        readOnly={readOnly || viewMode}
                        userId={userId}
                        onApprove={handleApproveAsset}
                        onSendForApproval={handleOpenApprovalModal}
                        approversDetails={approverDetails}
                      />
                    </Col>
                  </>
                )}

                {!copilotView
                  ? !showApproveBtn &&
                    !readOnly &&
                    !viewMode &&
                    hasContent && (
                      <>
                        <Col alignSelf="stretch">
                          <Divider direction="vertical" />
                        </Col>

                        <Button
                          icon={<SendIcon />}
                          title="Send for approval"
                          variant="plainLavenderInvert"
                          onClick={handleOpenApprovalModal}
                        />
                      </>
                    )
                  : null}

                {!viewMode && (
                  <>
                    <Col alignSelf="stretch">
                      <Divider direction="vertical" />
                    </Col>

                    <Col>
                      <Button ref={setMenuButtonEl} icon={<MoreIcon />} title="options" variant="plainLavenderInvert" />

                      <AssetMenu
                        onSendForApproval={handleOpenApprovalModal}
                        anchor={menuButtonEl}
                        canDownload={assets && assets?.length > 0}
                        canUpload={assets && assets?.length < 10}
                        onGenerateWithAIClick={generateWithAI}
                        onShowDetailsClick={handleShowDetails}
                        onDownloadClick={downloadAssets}
                        onUploadClick={onUploadClick}
                        onDeleteClick={onDelete}
                        viewMode={viewMode || readOnly}
                        copilotView={copilotView}
                        hideUploadButton={!assetOpen}
                      />
                    </Col>
                  </>
                )}
              </Row>
            }
            lastUpdated={lastEditDetails && getDateMonthStrDayShortYearFull(lastEditDetails.timestamp, '')}
            lastUpdatedUserAvatar={
              lastEditBy?.email && (
                <Avatar
                  alt={lastEditBy.name || lastEditBy.email}
                  color={lastEditBy.avatarColor}
                  size="sm"
                  src={lastEditBy.profileImage}
                />
              )
            }
            name={
              <EditableText
                type="body-m"
                placeholder="Add title"
                bold
                wrap="off"
                value={name}
                onChange={onNameChangeHandler}
                onClick={stopPropagation}
                readOnly={viewMode || readOnly}
              />
            }
          />

          <SendForApprovalModal assetId={id} opened={isApprovalModalOpened} onClose={handleCloseApprovalModal} />
        </>
      )
    }

    return (
      <>
        <AssetCardLayout
          onClick={handleShowDetails}
          ref={ref}
          className={className}
          head={
            <AssetCardLayout.Head>
              <Row justify="space-between" align="center">
                <Col>
                  <Select
                    readOnly={viewMode || readOnly}
                    highlight
                    renderIcon={false}
                    label="Asset type"
                    variant="plain"
                    value={type}
                    onChange={updateAssetType}
                    onClick={stopPropagation}
                    tabIndex={0}
                  >
                    {typeOptions.map((item) => (
                      <Option key={item.value} value={item.value}>
                        {item.label}
                      </Option>
                    ))}
                  </Select>
                </Col>
                <Col>
                  {readOnly && !viewMode && (
                    <Button variant="plainLavender" onClick={onRequestEditAccessClickHandler}>
                      Get edit access
                    </Button>
                  )}

                  {!viewMode && (
                    <Button
                      onClick={stopPropagation}
                      ref={setMenuButtonEl}
                      icon={<MoreIcon />}
                      title="options"
                      variant="plainLavenderInvert"
                      disabled={readOnly}
                    />
                  )}

                  <AssetMenu
                    anchor={menuButtonEl}
                    canDownload={assets && assets?.length > 0}
                    canUpload={assets && assets?.length < 10}
                    onGenerateWithAIClick={onGenerateWithAIClickHandler}
                    onShowDetailsClick={handleShowDetails}
                    onDownloadClick={downloadAssets}
                    onUploadClick={onUploadClick}
                    onDeleteClick={onDelete}
                    onSendForApproval={handleOpenApprovalModal}
                    viewMode={viewMode || readOnly}
                  />
                </Col>
              </Row>
              <Row>
                <EditableText
                  type="body-m"
                  placeholder="Add title"
                  bold
                  wrap="off"
                  value={name}
                  onChange={onNameChangeHandler}
                  onClick={stopPropagation}
                  readOnly={readOnly || viewMode}
                />
              </Row>
              <Row>
                <Col>
                  <TagsSelect
                    value={tagsIds}
                    displayLimit={3}
                    variant="dark"
                    displayTags
                    tags={tags}
                    type={TagTypes.ASSET}
                    onClick={stopPropagation}
                    onChange={updateAssetTags}
                    onCreate={createTag}
                    readOnly={readOnly || viewMode}
                  />
                </Col>
              </Row>
            </AssetCardLayout.Head>
          }
          aside={
            drag && (
              <AssetCardLayout.Aside className={styles.aside}>
                <Button icon={<DndIcon />} variant="plain" {...dndAttributes} {...dndListeners} />
              </AssetCardLayout.Aside>
            )
          }
          body={
            <AssetCardLayout.Body>
              <Row justify="center" align="center" className={styles.bodyContent}>
                <UploadAssetsInput
                  readOnly={readOnly || viewMode}
                  carouselView
                  ref={assetsInputRef}
                  value={assets}
                  onChange={updateAssets}
                  onLoadingChange={handleLoadingChange}
                />
                {/* FIXME can we simplify it? */}
                {type === AssetTypes.COPY ? (
                  <RichTextEditor
                    className={cn(styles.editableContent, !drag && 'pl-3', 'pr-3 pt-2 pb-2')}
                    initialContent={content}
                    tableEnabled
                    googleDocsEnabled
                    confluenceEnabled
                    readOnly={readOnly || viewMode}
                    placeholder="Drag and drop a file, write copy, or add a link..."
                    onCreate={editorProps.onCreate}
                    onDestroy={editorProps.onDestroy}
                    onChange={updateAssetContent}
                    onClick={stopPropagation}
                  />
                ) : !loading && assets?.length === 0 && content?.length > 0 ? (
                  <RichTextEditor
                    className={cn(styles.editableContent, !drag && 'pl-3', 'pr-3 pt-2 pb-2')}
                    initialContent={content}
                    tableEnabled
                    googleDocsEnabled
                    confluenceEnabled
                    readOnly={readOnly || viewMode}
                    placeholder="Drag and drop a file, write copy, or add a link..."
                    onCreate={editorProps.onCreate}
                    onDestroy={editorProps.onDestroy}
                    onChange={updateAssetContent}
                    onClick={stopPropagation}
                  />
                ) : !loading && assets?.length > 0 ? null : (
                  <Button
                    variant="dashed"
                    icon={<PlusIcon height={61} width={61} />}
                    disabled={viewMode}
                    onClick={(e) => {
                      stopPropagation(e)
                      handleShowDetails()
                    }}
                  />
                )}
              </Row>
            </AssetCardLayout.Body>
          }
          footer={
            <AssetCardLayout.Footer>
              <Row align="end" justify="space-between" gutterX="1" className={styles.footerContent}>
                <Editor user={lastEditBy} />

                <Approvals
                  approversDetails={approverDetails}
                  hasContent={hasContent}
                  readOnly={readOnly || viewMode}
                  onApproveClick={handleApproveAsset}
                  onSendForReviewClick={handleOpenApprovalModal}
                  showApproveBtn={showApproveBtn}
                />
              </Row>
            </AssetCardLayout.Footer>
          }
        />

        <SendForApprovalModal assetId={id} opened={isApprovalModalOpened} onClose={handleCloseApprovalModal} />
      </>
    )
  }
)

export default Asset
